"use client"

import { MoreHorizontal, Palette, Trash2, Copy, Calendar } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { Empty } from "../../../components/ui/empty";
import type { ColorTable } from "../../../lib/types";
import { getContrastColor } from "../../../lib/svg-color-extractor";
import { deleteColorTable } from "../../../API";

interface ColorTableGridProps {
  tables: ColorTable[]
  onDelete?: (id: string) => void
  onDuplicate?: (table: ColorTable) => void
}

export function ColorTableGrid({ tables, onDelete, onDuplicate }: ColorTableGridProps) {
  if (tables.length === 0) {
    return (
      <Empty
        className="py-12"
        //icon={<Palette className="size-12 text-muted-foreground" />}
        title="Nenhuma tabela de cores"
      //description="Crie sua primeira tabela de cores fazendo upload de um arquivo SVG."
      />
    )
  }

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(date))
  }

  const copyColorsToClipboard = async (colors: { hex: string }[]) => {
    const hexList = colors.map((c) => c.hex).join(", ")
    await navigator.clipboard.writeText(hexList)
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-h-[80vh] overflow-y-auto">
      {tables.map((table) => (
        <Card key={table.id} className="group relative overflow-hidden !left-[0px]">
          {/* Color Stripe */}
          <div className="h-2 flex">
            {table.colors.slice(0, 10).map((color, index) => (
              <div
                key={`${color.hex}-${index}`}
                className="flex-1"
                style={{ backgroundColor: color.hex }}
              />
            ))}
            {table.colors.length === 0 && (
              <div className="flex-1 bg-muted" />
            )}
          </div>

          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <CardTitle className="truncate text-base">
                  {table.name}
                </CardTitle>
                {table.description && (
                  <CardDescription className="line-clamp-2 mt-1">
                    {table.description}
                  </CardDescription>
                )}
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <MoreHorizontal className="size-4" />
                    <span className="sr-only">Menu</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => copyColorsToClipboard(table.colors)}
                  >
                    <Copy className="size-4 mr-2" />
                    Copiar cores
                  </DropdownMenuItem>
                  {/**{onDuplicate && (
                    <DropdownMenuItem onClick={() => onDuplicate(table)}>
                      <Copy className="size-4 mr-2" />
                      Duplicar
                    </DropdownMenuItem>
                  )} */}
                  {onDelete && (
                    <DropdownMenuItem
                      onClick={() => {
                        onDelete(table.id);
                        deleteColorTable(table.id)
                      }}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="size-4 mr-2" />
                      Excluir
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Colors Grid */}
            <div className="flex flex-wrap gap-1.5">
              {table.colors.slice(0, 12).map((color, index) => (
                <div
                  key={`${color.hex}-${index}`}
                  className="group/color relative"
                  title={`${color.hex} (${color.count}x)`}
                >
                  <div
                    className="size-8 rounded-md border border-border/50 transition-transform hover:scale-110 cursor-pointer"
                    style={{ backgroundColor: color.hex }}
                  />
                  <span
                    className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] font-mono opacity-0 group-hover/color:opacity-100 transition-opacity whitespace-nowrap bg-popover px-1 rounded"
                    style={{ color: getContrastColor(color.hex) === "#FFFFFF" ? "inherit" : "inherit" }}
                  >
                    {color.hex}
                  </span>
                </div>
              ))}
              {table.colors.length > 12 && (
                <div className="size-8 rounded-md bg-secondary flex items-center justify-center text-xs text-muted-foreground">
                  +{table.colors.length - 12}
                </div>
              )}
            </div>

            {/* Footer Info */}
            <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-border/50">
              <div className="flex items-center gap-1">
                <Palette className="size-3" />
                <span>{table.colors.length} cores</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="size-3" />
                <span>{formatDate(table.createdAt)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}